import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpFromLine } from "lucide-react";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

const METHODS = ["Easypaisa", "JazzCash", "Bank Transfer", "USDT (TRC20)"];

export default function Withdrawal() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [withdrawals, setWithdrawals] = useState(null);
  const [form, setForm] = useState({ method: "Easypaisa", account_details: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const [cap, setCap] = useState(null);

  const load = () => api.get("/withdrawals").then(({ data }) => setWithdrawals(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/capital/summary").then(({ data }) => setCap(data)).catch(() => {});
  }, []);
  if (!withdrawals) return <Loader />;

  const availableBalance = cap ? cap.available_balance : (user?.balance || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (Number(form.amount) < 100) { toast.error("Minimum withdrawal amount is Rs 100"); return; }
    if (Number(form.amount) > availableBalance) { toast.error(`Amount exceeds available balance (${fmtPKR(availableBalance)}). Capital is locked.`); return; }
    if (!form.account_details.trim()) { toast.error("Enter your account details"); return; }
    setBusy(true);
    try {
      await api.post("/withdrawals", { ...form, amount: Number(form.amount) });
      toast.success("Withdrawal request submitted!");
      const me = await api.get("/auth/me"); setUser(me.data);
      const c = await api.get("/capital/summary"); setCap(c.data);
      setForm({ method: "Easypaisa", account_details: "", amount: "" }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title={t("withdrawal")} subtitle={`Available: ${fmtPKR(availableBalance)}${cap && cap.locked_capital > 0 ? ` · Locked capital: ${fmtPKR(cap.locked_capital)}` : ""}`} />
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <form onSubmit={submit} className="ec-card p-6 space-y-4">
          <div>
            <Label>{t("withdrawal_method")}</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="withdraw-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>{t("account_details")}</Label><Textarea data-testid="withdraw-account" value={form.account_details} onChange={(e) => setForm({ ...form, account_details: e.target.value })} required rows={3} className="mt-1" placeholder="Account title, number / IBAN / wallet address" /></div>
          <div><Label>{t("withdraw_amount")} (PKR)</Label><Input data-testid="withdraw-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" placeholder="Minimum Rs 100" /></div>
          <Button type="submit" disabled={busy} data-testid="withdraw-submit" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold"><ArrowUpFromLine className="w-4 h-4 me-2" />{busy ? "..." : t("submit_withdrawal")}</Button>
        </form>

        <div>
          <h3 className="font-head text-lg font-semibold text-white mb-4">{t("my_withdrawals")}</h3>
          {withdrawals.length === 0 ? <Empty label="No withdrawals yet." /> : (
            <div className="space-y-3">
              {withdrawals.map((w) => (
                <div key={w.id} className="ec-card p-4" data-testid={`withdrawal-${w.id}`}>
                  <div className="flex items-center justify-between">
                    <div><p className="font-semibold text-white">{fmtPKR(w.amount)}</p><p className="text-xs text-gray-500 keep-ltr">{w.method}</p></div>
                    <Badge className={statusColor(w.status)}>{w.status}</Badge>
                  </div>
                  {w.reason && <p className="text-xs text-red-400 mt-2">{w.reason}</p>}
                  <p className="text-xs text-gray-600 mt-1">{fmtDate(w.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
